import { cn } from "@/utils/cn";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { forwardRef, memo } from "react";

/**
 * Button — shadcn primitive migrated to class-selector + co-located CSS.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see shadcn-authoring.md
 * commit b35c0a615). First execution of the shadcn-authoring template.
 *
 * Public API preserved exactly: variant + size remain string-typed enums;
 * asChild + Slot polymorphic-render preserved verbatim; React.ButtonHTMLAttributes
 * spread preserved.
 *
 * The variant and size axes are data-variant / data-size only. A primitive
 * that renders its own <button> (AlertDialogAction / AlertDialogCancel) puts
 * dw-button and the two attributes on it rather than nesting a Button.
 *
 * Source: docs/implementation-blueprint.md §8.3 (class-selector pattern);
 *         .claude/agent-prompts/shadcn-authoring.md (binding template, commit b35c0a615);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 * src/components/ui/atoms/CountBadge.tsx (atom precedent);
 * src/components/ui/molecules/PageHeader.tsx (molecule precedent, commit f661abe91).
 */
/**
 * `bare` contributes NO skin — no fill, no hover wash. For a component that
 * IS its own control species and wants only the base affordances (the flex
 * row, the focus ring, the disabled state, the icon sizing). Seven components
 * used to alias `const ButtonEl = "button"` to dodge the raw-button rule for
 * exactly this; `variant="bare" size="auto"` is that, on the atom.
 */
export type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "bare";
/**
 * `auto` defers height + padding to consumer Tailwind utilities. Use when
 * wrapping content that needs flexible vertical space (e.g., list rows with
 * avatar + multi-line text). The other sizes lock height via attribute-selector
 * CSS at specificity that beats raw `h-auto` — `auto` is the explicit escape.
 */
export type ButtonSize = "default" | "sm" | "lg" | "icon" | "auto";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Visual treatment. @default "default" */
    variant?: ButtonVariant;
    /** Sizing scale. @default "default" */
    size?: ButtonSize;
    /** Render as the immediate child element instead of `<button>` (Radix Slot pattern). */
    asChild?: boolean;
    /** Slide an icon button's label out on hover or keyboard focus. */
    revealLabel?: string;
    /** "block" for a Button that holds block content (a card, a multi-line row):
     *  its text wraps and starts at the line's start. @default inline */
    layout?: "block";
}

const Button = memo(
    forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant = "default", size = "default", asChild = false, revealLabel, layout, children, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp ref={ref} className={cn("dw-button", className)} data-variant={variant} data-size={size} data-layout={layout} data-reveal-label={revealLabel ? "true" : undefined} aria-label={revealLabel} data-component="Button" data-location="src/components/ui/shadcn/button.tsx" {...props}>
                {revealLabel ? (
                    <Slottable child={children}>
                        {(icon) => <>
                            <span className="dw-button__reveal-icon" aria-hidden="true">{icon}</span>
                            <span className="dw-button__reveal-label" aria-hidden="true"><span>{revealLabel}</span></span>
                        </>}
                    </Slottable>
                ) : children}
            </Comp>
        );
    })
);

Button.displayName = "Button";

export { Button };
